import { useEffect, useRef, useState, type CSSProperties } from 'react'
import './Spinner.css'

/** The logo's outline, traced from public/logo/logo.svg. */
const LOGO_PATH =
  'M489.544 599.86C509.508 599.86 528.017 603.345 545.069 610.313C562.122 617.282 576.887 627.154 589.364 639.929C602.258 652.317 612.24 666.834 619.311 683.48C626.797 700.127 630.54 718.129 630.54 737.485C630.54 756.454 627.005 774.262 619.935 790.908C617.162 797.284 613.971 803.39 610.367 809.229C579.434 819.461 546.364 825 512 825C455.32 825 402.158 809.934 356.302 783.588C351.134 769.069 348.548 753.702 348.548 737.485C348.548 718.129 352.083 700.127 359.153 683.48C366.224 666.834 376.207 652.317 389.101 639.929C401.994 627.154 416.967 617.282 434.02 610.313C451.072 603.345 469.58 599.86 489.544 599.86ZM512 199C684.865 199 825 339.135 825 512C825 603.047 786.124 685.013 724.068 742.208C724.102 740.639 724.121 739.065 724.121 737.485C724.121 708.064 717.883 680.19 705.405 653.865C693.344 627.541 676.291 604.312 654.247 584.182C641.953 572.519 628.584 562.296 614.141 553.51C618.091 550.857 621.947 548.127 625.706 545.317C653.003 524.917 672.371 502.929 683.808 479.356C694.996 455.451 699.455 431.912 697.184 408.74C694.912 385.568 686.818 364.674 672.905 346.058C659.737 328.438 646.185 315.51 632.247 307.274C618.061 298.706 603.825 293.543 589.54 291.784C575.007 289.693 560.426 289.968 545.799 292.61C531.256 294.671 517.166 297.688 503.532 301.66C488.651 306.046 473.73 309.685 458.77 312.576C444.142 315.218 430.847 315.827 418.888 314.402C407.013 312.397 397.845 307.073 391.386 298.43C381.945 285.797 379.358 272.964 383.625 259.931C387.893 246.897 397.849 234.533 413.495 222.84C421.493 216.862 430.57 211.634 440.724 207.152C463.62 201.82 487.48 199 512 199ZM292.83 288.543C292.911 290.39 293.034 292.24 293.198 294.093C295.554 316.683 304.682 338.618 320.583 359.894C333.751 377.513 348.095 390.108 363.612 397.682C379.214 404.673 395.281 408.469 411.812 409.067C428.427 409.085 444.839 407.441 461.047 404.136C477.254 400.831 492.633 397.11 507.181 392.973C519.567 389.415 531.619 387.144 543.335 386.159C555.384 384.926 565.975 385.56 575.107 388.061C584.573 390.313 592.039 395.096 597.505 402.409C605.455 413.047 608.67 423.597 607.15 434.059C605.963 444.272 601.87 454.068 594.869 463.444C587.953 472.24 580.167 479.872 571.512 486.341C562.857 492.809 552.375 498.57 540.068 503.623C527.761 508.676 514.708 512.732 500.91 515.79C495.746 516.723 490.152 517.716 484.469 518.601C477.798 519.639 424.819 524.903 397.834 535.984C369.552 547.598 344.597 563.664 322.97 584.182C301.758 604.312 285.121 627.541 273.06 653.865C266.752 667.632 262.094 681.822 259.085 696.436C221.301 644.711 199 580.96 199 512C199 424.477 234.924 345.345 292.83 288.543Z'

/** Çizimin tamamı: çizgi, sonra siyah dolgu. Spinner.css'teki --ye-draw. */
export const DRAW_MS = 2000

/**
 * Arka arkaya açılan bekleme ekranlarının ortak saati.
 *
 * Yenilemeden sonra önce giriş kontrolü, sonra menü için ayrı bir bekleme
 * ekranı açılıyor; ikisi ayrı bileşen, ve her biri çizimi baştan başlatınca
 * logo iki kez çiziliyor, sayfa titriyordu. Hepsi aynı başlangıç anına bakıyor:
 * yeni açılan, öncekinin kaldığı yerden devam ediyor.
 *
 * sessionStorage'da, çünkü aşağı çekip yenileme sayfanın kendisini yeniden
 * yüklüyor: yenilemeden önce dolan logo, sonrasında dolu haliyle açılıyor,
 * yeniden çizilmiyor. Son bekleme ekranı kapanalı bir saniyeyi geçtiyse yeni
 * bir bekleyiş sayılıyor ve çizim baştan başlıyor.
 */
const CLOCK_KEY = 'adissoo.spin'
const GAP_MS = 1000

function readClock(): { start: number; seen: number } | null {
  try {
    const raw = sessionStorage.getItem(CLOCK_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeClock(start: number) {
  try {
    sessionStorage.setItem(CLOCK_KEY, JSON.stringify({ start, seen: Date.now() }))
  } catch {
    // Depolama yoksa her bekleme ekranı kendi çizimini yapar, o kadar.
  }
}

function useDrawClock(): { start: number; elapsed: number } {
  const [clock] = useState(() => {
    const now = Date.now()
    const prev = readClock()
    const start = prev && now - prev.seen < GAP_MS ? prev.start : now
    writeClock(start)
    return { start, elapsed: now - start }
  })

  // Görünür olduğu sürece "hala bekleniyor" diye işaretliyor; sayfa giderken
  // de (yenileme React'e kapanma fırsatı vermiyor).
  useEffect(() => {
    const mark = () => writeClock(clock.start)
    const timer = window.setInterval(mark, 250)
    window.addEventListener('pagehide', mark)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('pagehide', mark)
      mark()
    }
  }, [clock.start])

  return clock
}

/**
 * Tam ekran bekleme, çizim bitmeden kalkmıyor.
 *
 * Veriler çoğu zaman iki saniyeden önce geliyor ve sayfa bekleme ekranını
 * hemen söküyordu: logo dolgusu gelmeden kayboluyordu. Sökülen ekranın bir
 * kopyası çizimin geri kalanı kadar sayfanın üstünde duruyor, aynı saatle,
 * yani logo kaldığı yerden devam edip dolu haliyle bitiyor, sonra kısa bir
 * geçişle kalkıyor.
 *
 * DOM'da ve React'in dışında, çünkü ekranı söken bileşenin kendisi artık yok.
 */
function holdUntilDrawn(node: HTMLElement | null, start: number, shownAt: number) {
  const left = start + DRAW_MS - Date.now()
  if (!node || left <= 0) return
  // Göz açıp kapayıncaya kadar kalktıysa tutulmuyor (28 Eki 2026): kayıtlı
  // kopya sayesinde sayfa anında geliyor, logo yalnız beklemeyi uzatıyordu.
  if (Date.now() - shownAt < QUICK_MS) return
  const ghost = node.cloneNode(true) as HTMLElement
  ghost.style.setProperty('--ye-offset', `${start - Date.now()}ms`)
  document.body.appendChild(ghost)
  window.setTimeout(() => {
    writeClock(start)
    ghost.classList.add('is-leaving')
    window.setTimeout(() => ghost.remove(), LEAVE_MS)
  }, left)
}

const LEAVE_MS = 150
/** Bundan kısa süre görünen bekleme ekranı çizimini tamamlamak için tutulmaz. */
const QUICK_MS = 450

/**
 * Waiting, drawn as the logo drawing itself.
 *
 * The outline is traced first, a line travelling the whole shape, the way a
 * band wraps round it, and the fill arrives behind it once the circuit
 * closes. Once, and it stays solid, see Spinner.css.
 *
 * Inline rather than an <img>: the path has to be animated, and a file
 * referenced from a src is a picture the page cannot reach into. It also
 * means nothing is fetched, this shows up when the network is the thing
 * being waited on.
 *
 * The dash length is a round number larger than the path (getTotalLength is
 * ~4800 here), so no measurement runs at render. Too large and the line
 * crawls; this one is close enough that the trace reads as continuous.
 */
/**
 * @param full  Fills the screen instead of sitting inline.
 *
 * Two jobs behind one component, and they want different sizes. A panel
 * waiting for its list needs a mark beside a line of text; a screen that has
 * nothing on it yet is the mark, and at 34px it read as a stray glyph in a
 * corner of an empty page. `full` is the second case: the page's own ground
 * and the logo drawn large enough to be the thing on screen.
 */
export function Spinner({ label, full }: { label?: string; full?: boolean }) {
  const clock = useDrawClock()
  const ref = useRef<HTMLDivElement>(null)
  const shownAt = useRef(Date.now())

  useEffect(() => {
    // Söküldükten sonra ref boşalıyor; düğüm şimdiden alınıyor.
    const node = ref.current
    return () => {
      if (full) holdUntilDrawn(node, clock.start, shownAt.current)
    }
  }, [full, clock.start])
  return (
    <div
      ref={ref}
      className={[
        'ye-spinner',
        full ? 'ye-spinner--full' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--ye-offset': `${-clock.elapsed}ms` } as CSSProperties}
    >
      <svg
        className="ye-spinner__logo"
        viewBox="0 0 1024 1024"
        role="img"
        aria-label={label ?? 'Yükleniyor'}
      >
        {/* The ground stays put: it is the badge the mark sits in, not part
            of what is being drawn. */}
        <circle className="ye-spinner__ground" cx="512" cy="512" r="500" />
        <path className="ye-spinner__fill" d={LOGO_PATH} />
        <path className="ye-spinner__trace" d={LOGO_PATH} />
      </svg>
      {/* Etiket ekrana yazılmıyor, yalnızca ekran okuyucuya gidiyor: logo
          tek başına "bekleyin" diyor, altındaki "Yükleniyor…" aynı şeyi
          ikinci kez söylüyordu. */}
    </div>
  )
}
